import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RoleService from '../../services/roleService';
import StoreService from '../../services/storeService';
import api from '../../services/api';
import ScrollableDialog from '../common/ScrollableDialog';
import {
  getPermissionName,
  groupPermissionsByCategory,
  orderedPermissionCategoryEntries,
} from '../../utils/permissionTranslations';
import PermissionInfoIcon from '../common/PermissionInfoIcon';

const RoleEditModal = ({ open, onClose, onSuccess, role, storeId, isCreating }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [permissionsTranslations, setPermissionsTranslations] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [roleNames, setRoleNames] = useState({});
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [permissionsRes, storeRes] = await Promise.all([
        api.get('/permissions'),
        StoreService.getStore(storeId)
      ]);

      const permissionsData = permissionsRes.data;
      
      if (permissionsData && typeof permissionsData === 'object' && !Array.isArray(permissionsData)) {
        setPermissionsTranslations(permissionsData);
        setPermissions(Object.keys(permissionsData));
      } else {
        setPermissions(permissionsData || []);
        setPermissionsTranslations({});
      }

      const store = storeRes;
      const languages = store.supportedLanguages || store.SupportedLanguages || [];
      setSupportedLanguages(languages);

      if (role) {
        const rolePermissions = role.permissions ? (Array.isArray(role.permissions) ? role.permissions : Object.keys(role.permissions)) : [];
        setSelectedPermissions(new Set(rolePermissions));
        
        const names = {};
        languages.forEach(lang => {
          const langKey = lang.toUpperCase();
          if (role.name && role.name[langKey]) {
            names[lang] = role.name[langKey];
          }
        });
        setRoleNames(names);
      } else {
        const names = {};
        languages.forEach(lang => {
          names[lang] = '';
        });
        setRoleNames(names);
        setSelectedPermissions(new Set());
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(t('roles.modal.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [storeId, role, t]);

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      resetForm();
    }
  }, [open, role, storeId, loadData]);

  const resetForm = () => {
    setRoleNames({});
    setSelectedPermissions(new Set());
    setError(null);
    setValidationErrors({});
    setCurrentTab(0);
  };

  const handleNameChange = (language, value) => {
    setRoleNames(prev => ({
      ...prev,
      [language]: value
    }));
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`name_${language}`];
      return newErrors;
    });
  };

  const handlePermissionToggle = (permission) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permission)) {
        newSet.delete(permission);
      } else {
        newSet.add(permission);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedPermissions(new Set(permissions));
  };

  const handleDeselectAll = () => {
    setSelectedPermissions(new Set());
  };

  const validate = () => {
    const errors = {};
    
    supportedLanguages.forEach(lang => {
      if (!roleNames[lang] || roleNames[lang].trim() === '') {
        errors[`name_${lang}`] = t('roles.modal.form.nameRequired');
      }
    });

    if (selectedPermissions.size === 0) {
      errors.permissions = t('roles.modal.form.permissionsRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const nameDict = {};
      supportedLanguages.forEach(lang => {
        const langKey = lang.toUpperCase();
        nameDict[langKey] = roleNames[lang];
      });

      const roleData = {
        storeId: storeId,
        name: nameDict,
        permissions: Array.from(selectedPermissions)
      };

      if (isCreating) {
        await RoleService.createRole(roleData);
      } else {
        await RoleService.updateRole(role.id, roleData);
      }

      onSuccess();
    } catch (err) {
      console.error('Failed to save role:', err);
      setError(isCreating ? t('roles.modal.error.create') : t('roles.modal.error.update'));
    } finally {
      setLoading(false);
    }
  };

  const groupedPermissions = groupPermissionsByCategory(permissions);

  const languageLabels = {
    'EN': 'English',
    'PT': 'Português',
    'ES': 'Español'
  };

  const getPermissionLabel = (permission) => {
    let permissionTranslations = null;
    
    if (role && role.permissions && typeof role.permissions === 'object' && !Array.isArray(role.permissions)) {
      permissionTranslations = role.permissions[permission];
    }
    
    if (!permissionTranslations && permissionsTranslations && permissionsTranslations[permission]) {
      permissionTranslations = permissionsTranslations[permission];
    }
    
    if (permissionTranslations) {
      return getPermissionName(permissionTranslations, i18n.language);
    }
    
    return permission;
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={isCreating ? t('roles.modal.create.title') : t('roles.modal.edit.title')}
      subtitle={isCreating ? t('roles.modal.create.subtitle') : t('roles.modal.edit.subtitle')}
      actionsSx={{ justifyContent: 'flex-end' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={loading}>
            {t('roles.modal.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? t('roles.modal.saving') : t('roles.modal.save')}
          </Button>
        </>
      )}
    >
        {loading && Object.keys(roleNames).length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!loading && (
          <Box sx={{ mt: 2 }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
              <Tab label={t('roles.modal.form.name')} />
              <Tab label={t('roles.modal.form.permissions')} />
            </Tabs>

            {currentTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {supportedLanguages.map(lang => {
                  const langKey = lang.toUpperCase();
                  return (
                    <TextField
                      sx={{ mt: 1 }}
                      key={lang}
                      label={`${t('roles.modal.form.name')} (${languageLabels[langKey] || lang})`}
                      value={roleNames[lang] || ''}
                      onChange={(e) => handleNameChange(lang, e.target.value)}
                      error={!!validationErrors[`name_${lang}`]}
                      helperText={validationErrors[`name_${lang}`]}
                      fullWidth
                      required
                    />
                  );
                })}
              </Box>
            )}

            {currentTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">{t('roles.modal.form.permissions')}</Typography>
                  <Box>
                    <Button size="small" onClick={handleSelectAll}>
                      {t('roles.modal.form.selectAll')}
                    </Button>
                    <Button size="small" onClick={handleDeselectAll}>
                      {t('roles.modal.form.deselectAll')}
                    </Button>
                  </Box>
                </Box>

                {validationErrors.permissions && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {validationErrors.permissions}
                  </Alert>
                )}

                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 3,
                    maxHeight: '400px',
                    overflowY: 'auto',
                    pr: 1
                  }}
                >
                  {orderedPermissionCategoryEntries(groupedPermissions).map(([category, perms]) => (
                    <FormControl key={category} component="fieldset">
                      <FormLabel component="legend">
                        {t(`roles.modal.form.categories.${category}`)}
                      </FormLabel>
                      <FormGroup>
                        {perms.map(perm => (
                          <FormControlLabel
                            key={perm}
                            control={
                              <Checkbox
                                checked={selectedPermissions.has(perm)}
                                onChange={() => handlePermissionToggle(perm)}
                              />
                            }
                            label={(
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{getPermissionLabel(perm)}</span>
                                <PermissionInfoIcon
                                  permissionTranslations={permissionsTranslations[perm]}
                                  language={i18n.language}
                                />
                              </Box>
                            )}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
    </ScrollableDialog>
  );
};

export default RoleEditModal;

